import { generatedApi } from './generated-api';
import type {
  GetListDialogsDialogListPostApiArg,
  GetListDialogsDialogListPostApiResponse,
} from './generated-api';

export const dialogsListApi = generatedApi.injectEndpoints({
  endpoints: (build) => ({
    getListDialogsDialogListPostQuery: build.query<
      GetListDialogsDialogListPostApiResponse,
      GetListDialogsDialogListPostApiArg
    >({
      query: (queryArg) => ({
        url: `/dialog/list`,
        method: 'POST',
        body: queryArg.dialogListFilters,
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const channelIds = queryArgs?.dialogListFilters?.channel_ids ?? null;
        const normalizedChannelIds = Array.isArray(channelIds)
          ? [...channelIds].sort((a, b) => a - b)
          : null;
        const search = (queryArgs?.dialogListFilters as { q?: string })?.q ?? null;
        const organizationId = queryArgs?.dialogListFilters?.organization_id ?? null;
        const isLead = queryArgs?.dialogListFilters?.is_lead ?? null;
        return { endpointName, organizationId, channelIds: normalizedChannelIds, search, isLead };
      },
      merge: (currentCache, newItems, { arg }) => {
        if (!newItems?.items) return;

        const isFirstPage = !arg?.offset || arg.offset === 0;

        const existing = isFirstPage ? [] : (currentCache.items ?? []);
        const combined = [...existing, ...newItems.items];

        const uniqueByClientId = new Map<number, (typeof combined)[number]>();
        combined.forEach((dialog) => uniqueByClientId.set(dialog.client_id, dialog));

        const sorted = Array.from(uniqueByClientId.values()).sort((a, b) => {
          const aTime = a.last_message_at ?? '';
          const bTime = b.last_message_at ?? '';
          return bTime.localeCompare(aTime);
        });

        currentCache.items = sorted;
        currentCache.count = newItems.count ?? currentCache.count ?? sorted.length;
        currentCache.offset = arg?.offset ?? newItems.offset ?? 0;
        currentCache.limit = arg?.limit ?? newItems.limit ?? currentCache.limit;
      },
      forceRefetch({ currentArg, previousArg }) {
        const serializeChannels = (value?: number[] | null) =>
          Array.isArray(value) ? [...value].sort((a, b) => a - b).join(',') : '';
        const currentChannels = serializeChannels(currentArg?.dialogListFilters?.channel_ids ?? null);
        const previousChannels = serializeChannels(
          previousArg?.dialogListFilters?.channel_ids ?? null,
        );
        const currentSearch = (currentArg?.dialogListFilters as { q?: string })?.q ?? '';
        const previousSearch = (previousArg?.dialogListFilters as { q?: string })?.q ?? '';
        const currentOrganizationId = currentArg?.dialogListFilters?.organization_id ?? null;
        const previousOrganizationId = previousArg?.dialogListFilters?.organization_id ?? null;
        const currentIsLead = currentArg?.dialogListFilters?.is_lead ?? null;
        const previousIsLead = previousArg?.dialogListFilters?.is_lead ?? null;

        return (
          currentArg?.offset !== previousArg?.offset ||
          currentArg?.limit !== previousArg?.limit ||
          currentChannels !== previousChannels ||
          currentSearch !== previousSearch ||
          currentOrganizationId !== previousOrganizationId ||
          currentIsLead !== previousIsLead
        );
      },
      providesTags: ['dialog'],
    }),
  }),
  overrideExisting: false,
});

export const { useGetListDialogsDialogListPostQueryQuery } = dialogsListApi;
